import React, { useState, useEffect } from 'react';
import { AIQuotationTemplate, AITemplateVariable } from '../../types/aiQuotation';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { 
  DocumentTextIcon,
  PlusIcon,
  PencilSquareIcon,
  TrashIcon,
  EyeIcon,
  CheckIcon,
  XMarkIcon,
  TagIcon,
  DocumentDuplicateIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';
import { Card } from '../common/Card';
import Modal from '../common/Modal';
import { Input } from '../common/Input';
import { Button } from '../common/Button';
import CommonPageHeader from '../common/CommonPageHeader';
import { toast } from 'react-hot-toast';

interface TemplateManagerProps {
  isOpen: boolean;
  onClose: () => void;
  templates: AIQuotationTemplate[];
  onTemplateCreate?: (template: Omit<AIQuotationTemplate, 'id' | 'createdAt' | 'updatedAt' | 'usageCount'>) => void;
  onTemplateUpdate?: (id: string, template: Partial<AIQuotationTemplate>) => void;
  onTemplateDelete?: (id: string) => void;
  onTemplateRefresh?: () => void;
}

interface TemplateFormData {
  name: string;
  description: string;
  category: 'general' | 'service' | 'product' | 'consulting' | 'maintenance' | 'custom';
  content: string;
  variables: AITemplateVariable[];
}

const TemplateManager: React.FC<TemplateManagerProps> = ({
  isOpen,
  onClose,
  templates,
  onTemplateCreate,
  onTemplateUpdate,
  onTemplateDelete,
  onTemplateRefresh
}) => {
  const { user } = useAuth();
  const { t } = useLanguage();
  
  // State management
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<AIQuotationTemplate | null>(null);
  const [previewTemplate, setPreviewTemplate] = useState<AIQuotationTemplate | null>(null);
  const [formData, setFormData] = useState<TemplateFormData>({
    name: '',
    description: '',
    category: 'general',
    content: '',
    variables: []
  });

  const categories = [
    { value: 'all', label: 'Összes sablon', color: 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300' },
    { value: 'general', label: 'Általános', color: 'bg-blue-100 dark:bg-blue-900/20 text-blue-800 dark:text-blue-300' },
    { value: 'service', label: 'Szolgáltatás', color: 'bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-300' },
    { value: 'product', label: 'Termék', color: 'bg-purple-100 dark:bg-purple-900/20 text-purple-800 dark:text-purple-300' },
    { value: 'consulting', label: 'Tanácsadás', color: 'bg-orange-100 dark:bg-orange-900/20 text-orange-800 dark:text-orange-300' },
    { value: 'maintenance', label: 'Karbantartás', color: 'bg-yellow-100 dark:bg-yellow-900/20 text-yellow-800 dark:text-yellow-300' },
    { value: 'custom', label: 'Egyedi', color: 'bg-gray-100 dark:bg-gray-900/20 text-gray-800 dark:text-gray-300' }
  ];

  // Filter templates
  const filteredTemplates = templates.filter(template => {
    const matchesCategory = selectedCategory === 'all' || template.category === selectedCategory;
    const matchesSearch = !searchTerm || 
      template.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      template.description.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const getCategoryInfo = (category: string) => {
    return categories.find(cat => cat.value === category) || categories[0];
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      category: 'general',
      content: '',
      variables: []
    });
  };

  // Handle create template
  const handleCreateTemplate = () => {
    resetForm();
    setEditingTemplate(null);
    setIsCreateModalOpen(true);
  };

  // Handle edit template
  const handleEditTemplate = (template: AIQuotationTemplate) => {
    setFormData({
      name: template.name,
      description: template.description,
      category: template.category,
      content: template.content,
      variables: [...template.variables]
    });
    setEditingTemplate(template);
    setIsCreateModalOpen(true);
  };

  // Handle save template
  const handleSaveTemplate = () => {
    if (!formData.name.trim()) {
      toast.error('Kérjük adja meg a sablon nevét');
      return;
    }

    if (!formData.content.trim()) {
      toast.error('Kérjük adja meg a sablon tartalmát');
      return;
    }

    const templateData = {
      ...formData,
      organizationId: user?.organizationId || '',
      isActive: true,
      createdBy: user?.uid || ''
    };

    if (editingTemplate) {
      // Update existing template
      onTemplateUpdate?.(editingTemplate.id, templateData);
      toast.success('Sablon sikeresen frissítve');
    } else {
      // Create new template
      onTemplateCreate?.(templateData);
      toast.success('Sablon sikeresen létrehozva');
    }

    setIsCreateModalOpen(false);
    resetForm();
    setEditingTemplate(null);
  };

  // Handle delete template
  const handleDeleteTemplate = (template: AIQuotationTemplate) => {
    if (window.confirm(`Biztosan törli a "${template.name}" sablont?`)) {
      onTemplateDelete?.(template.id);
      toast.success('Sablon törölve');
    }
  };

  // Add variable to form
  const addVariable = () => {
    const newVariable: AITemplateVariable = {
      key: '',
      label: '',
      type: 'text',
      required: false
    };
    setFormData(prev => ({
      ...prev,
      variables: [...prev.variables, newVariable]
    }));
  };

  // Update variable in form
  const updateVariable = (index: number, field: keyof AITemplateVariable, value: any) => {
    setFormData(prev => ({
      ...prev,
      variables: prev.variables.map((variable, i) => 
        i === index ? { ...variable, [field]: value } : variable
      )
    }));
  };

  // Remove variable from form
  const removeVariable = (index: number) => {
    setFormData(prev => ({
      ...prev,
      variables: prev.variables.filter((_, i) => i !== index)
    }));
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Sablonok kezelése"
      size="7xl"
    >
      {/* Header with gradient background */}
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center space-x-4">
            {/* Search */}
            <div className="relative">
              <Input
                type="text"
                placeholder="Keresés sablonok között..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 w-64 bg-white/20 backdrop-blur-sm text-white placeholder-white/70 border-white/30"
              />
              <DocumentTextIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-white/70" />
            </div>

            {/* Refresh button */}
            <Button
              variant="outline"
              onClick={onTemplateRefresh}
              className="bg-white/20 backdrop-blur-sm text-white border-white/30 hover:bg-white/30 flex items-center space-x-2"
            >
              <ArrowPathIcon className="h-4 w-4" />
              <span>Frissítés</span>
            </Button>
          </div>

          {/* Create button */}
          <Button
            onClick={handleCreateTemplate}
            className="bg-white/20 backdrop-blur-sm text-white border-white/30 hover:bg-white/30 flex items-center space-x-2"
          >
            <PlusIcon className="h-4 w-4" />
            <span>Új sablon</span>
          </Button>
        </div>
      </div>

      <div className="p-6">
        {/* Category filter */}
        <div className="flex flex-wrap gap-2 mb-6">
          {categories.map((category) => (
            <button
              key={category.value}
              onClick={() => setSelectedCategory(category.value)}
              className={`px-3 py-1.5 text-sm font-medium rounded-full border transition-colors ${
                selectedCategory === category.value
                  ? 'bg-primary-100 dark:bg-primary-900/20 text-primary-800 dark:text-primary-300 border-primary-300 dark:border-primary-700'
                  : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-200 dark:hover:bg-gray-700'
              }`}
            >
              {category.label}
              {category.value !== 'all' && (
                <span className="ml-2 text-xs opacity-75">
                  ({templates.filter(t => t.category === category.value).length})
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Templates grid */}
        {filteredTemplates.length === 0 ? (
          <Card className="text-center py-12">
            <DocumentTextIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
              Nincsenek sablonok
            </h3>
            <p className="text-gray-500 dark:text-gray-400 mb-4">
              {searchTerm 
                ? `Nincs találat a "${searchTerm}" keresésre.`
                : selectedCategory === 'all' 
                  ? 'Még nincsenek létrehozott sablonok.'
                  : `Nincsenek sablonok a "${getCategoryInfo(selectedCategory).label}" kategóriában.`
              }
            </p>
            <Button onClick={handleCreateTemplate}>
              <PlusIcon className="h-4 w-4 mr-2" />
              Első sablon létrehozása
            </Button>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {filteredTemplates.map((template) => {
              const categoryInfo = getCategoryInfo(template.category);
              return (
                <div key={template.id} 
                     className="hover:shadow-md transition-all hover:scale-[1.02] border-l-4 border-r-4" 
                     style={{
                       borderLeftColor: getCategoryInfo(template.category).value === 'general' ? '#2563eb' :
                                      getCategoryInfo(template.category).value === 'service' ? '#16a34a' :
                                      getCategoryInfo(template.category).value === 'product' ? '#7c3aed' :
                                      getCategoryInfo(template.category).value === 'consulting' ? '#f97316' :
                                      getCategoryInfo(template.category).value === 'maintenance' ? '#eab308' : '#6b7280',
                       borderRightColor: template.isActive ? '#16a34a' : '#dc2626'
                     }}>
                  <Card>
                  <div className="p-4">
                    {/* Header */}
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1 min-w-0">
                        <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                          {template.name}
                        </h3>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 line-clamp-2">
                          {template.description}
                        </p>
                      </div>
                    </div>

                    {/* Category and usage */}
                    <div className="flex items-center justify-between mb-3">
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${categoryInfo.color} border-current`}>
                        <TagIcon className="h-3 w-3 mr-1" />
                        {categoryInfo.label}
                      </span>
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        {template.usageCount} használat
                      </span>
                    </div>

                    {/* Variables count */}
                    <div className="text-xs text-gray-500 dark:text-gray-400 mb-4">
                      {template.variables.length} változó
                    </div>

                    {/* Actions */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-1">
                        <button
                          onClick={() => setPreviewTemplate(template)}
                          className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                          title="Előnézet"
                        >
                          <EyeIcon className="h-4 w-4" />
                        </button>
                        
                        <button
                          onClick={() => handleEditTemplate(template)}
                          className="p-1.5 text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                          title="Szerkesztés"
                        >
                          <PencilSquareIcon className="h-4 w-4" />
                        </button>
                        
                        <button
                          onClick={() => handleDeleteTemplate(template)}
                          className="p-1.5 text-gray-400 hover:text-red-600 dark:hover:text-red-400 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                          title="Törlés"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>

                      {/* Status indicator */}
                      <div className={`w-2 h-2 rounded-full ${
                        template.isActive 
                          ? 'bg-green-500' 
                          : 'bg-gray-400'
                      }`} />
                    </div>
                  </div>
                  </Card>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Create/Edit Template Modal */}
      <Modal
        isOpen={isCreateModalOpen}
        onClose={() => {
          setIsCreateModalOpen(false);
          resetForm();
          setEditingTemplate(null);
        }}
        title={editingTemplate ? 'Sablon szerkesztése' : 'Új sablon létrehozása'}
        size="6xl"
      >
        {/* Modal header with gradient */}
        <div className="bg-gradient-to-r from-green-100 to-teal-100 dark:from-green-900/20 dark:to-teal-900/20 px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center">
            <DocumentDuplicateIcon className="w-6 h-6 text-green-600 dark:text-green-400 mr-3" />
            <div>
              <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100">
                {editingTemplate ? 'Sablon szerkesztése' : 'Új sablon létrehozása'}
              </h2>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {editingTemplate ? 'Módosítsa a sablon adatait' : 'Hozzon létre egy új ajánlat sablont'}
              </p>
            </div>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* Basic info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Sablon neve *
              </label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="pl. Általános szolgáltatási ajánlat"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Kategória *
              </label>
              <select
                value={formData.category}
                onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value as any }))}
                className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm text-gray-700 dark:text-gray-200 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
              >
                {categories.slice(1).map((category) => (
                  <option key={category.value} value={category.value}>
                    {category.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Leírás
              </label>
              <Input
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Rövid leírás a sablon céljáról és használatáról"
              />
            </div>
          </div>

          {/* Variables */}
          <Card>
            <div className="flex">
              <div className="p-4 bg-gradient-to-br from-amber-100 to-orange-100 dark:from-amber-900/20 dark:to-orange-900/20 flex items-center justify-center rounded-l-lg border-r border-gray-200 dark:border-gray-600">
                <TagIcon className="w-5 h-5 text-amber-600 dark:text-amber-400" />
              </div>
              <div className="p-4 flex-1">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-medium text-gray-800 dark:text-gray-200">
                    Sablon változók ({formData.variables.length})
                  </h3>
                  <Button
                    variant="outline"
                    onClick={addVariable}
                    className="flex items-center space-x-2"
                  >
                    <PlusIcon className="h-4 w-4" />
                    <span>Változó hozzáadása</span>
                  </Button>
                </div>

            <div className="space-y-4">
              {formData.variables.map((variable, index) => (
                <Card key={index}>
                  <div className="p-4">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Kulcs *
                        </label>
                        <Input
                          value={variable.key}
                          onChange={(e) => updateVariable(index, 'key', e.target.value)}
                          placeholder="pl. serviceType"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Címke *
                        </label>
                        <Input
                          value={variable.label}
                          onChange={(e) => updateVariable(index, 'label', e.target.value)}
                          placeholder="pl. Szolgáltatás típusa"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Típus
                        </label>
                        <select
                          value={variable.type}
                          onChange={(e) => updateVariable(index, 'type', e.target.value)}
                          className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm text-gray-700 dark:text-gray-200 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                        >
                          <option value="text">Szöveg</option>
                          <option value="textarea">Hosszú szöveg</option>
                          <option value="number">Szám</option>
                          <option value="date">Dátum</option>
                          <option value="select">Választó</option>
                        </select>
                      </div>

                      <div className="flex items-end space-x-2">
                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            checked={variable.required}
                            onChange={(e) => updateVariable(index, 'required', e.target.checked)}
                            className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                          />
                          <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Kötelező</span>
                        </label>
                        <Button
                          variant="outline"
                          onClick={() => removeVariable(index)}
                          className="p-2"
                        >
                          <TrashIcon className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Alapértelmezett érték
                        </label>
                        <Input
                          value={variable.defaultValue || ''}
                          onChange={(e) => updateVariable(index, 'defaultValue', e.target.value)}
                          placeholder="Opcionális alapértelmezett érték"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Leírás
                        </label>
                        <Input
                          value={variable.description || ''}
                          onChange={(e) => updateVariable(index, 'description', e.target.value)}
                          placeholder="Segítő szöveg a felhasználónak"
                        />
                      </div>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
              </div>
            </div>
          </Card>

          {/* Template content */}
          <Card>
            <div className="flex">
              <div className="p-4 bg-gradient-to-br from-indigo-100 to-purple-100 dark:from-indigo-900/20 dark:to-purple-900/20 flex items-center justify-center rounded-l-lg border-r border-gray-200 dark:border-gray-600">
                <DocumentTextIcon className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
              </div>
              <div className="p-4 flex-1">
                <h3 className="text-sm font-medium text-gray-800 dark:text-gray-200 mb-4">
                  Sablon tartalom (Markdown) *
                </h3>
                <textarea
                  value={formData.content}
                  onChange={(e) => setFormData(prev => ({ ...prev, content: e.target.value }))}
                  rows={12}
                  className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm text-gray-700 dark:text-gray-200 placeholder-gray-500 dark:placeholder-gray-400 focus:border-primary-500 focus:ring-1 focus:ring-primary-500 font-mono"
                  placeholder={`# Ajánlat - {{projectName}}

Tisztelt {{customerName}}!

## Szolgáltatás részletei
- **Típus**: {{serviceType}}
- **Időtartam**: {{duration}}

## Projekt leírása
{{projectDescription}}

## Szállítási határidő
{{deliveryDate}}

Az ajánlat {{validityPeriod}} érvényes.

Tisztelettel,
Csapatunk`}
                />
                <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                  Használjon {`{{változóKulcs}}`} formátumot a változók beillesztéséhez.
                </p>
              </div>
            </div>
          </Card>

          {/* Actions */}
          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700">
            <Button
              variant="outline"
              onClick={() => {
                setIsCreateModalOpen(false);
                resetForm();
                setEditingTemplate(null);
              }}
            >
              Mégse
            </Button>
            <Button onClick={handleSaveTemplate}>
              {editingTemplate ? 'Frissítés' : 'Létrehozás'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Preview Modal */}
      <Modal
        isOpen={!!previewTemplate}
        onClose={() => setPreviewTemplate(null)}
        title={`Sablon előnézet: ${previewTemplate?.name}`}
        size="4xl"
      >
        {previewTemplate && (
          <div className="space-y-6">
            {/* Modal header with gradient */}
            <div className="bg-gradient-to-r from-blue-100 to-indigo-100 dark:from-blue-900/20 dark:to-indigo-900/20 px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center">
                <EyeIcon className="w-6 h-6 text-blue-600 dark:text-blue-400 mr-3" />
                <div>
                  <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100">
                    {previewTemplate.name}
                  </h2>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Sablon előnézet és részletek
                  </p>
                </div>
              </div>
            </div>

            <div className="p-6 space-y-6">
            {/* Template info */}
            <Card>
              <div className="flex">
                <div className="p-4 bg-gradient-to-br from-green-100 to-teal-100 dark:from-green-900/20 dark:to-teal-900/20 flex items-center justify-center rounded-l-lg border-r border-gray-200 dark:border-gray-600">
                  <DocumentDuplicateIcon className="w-5 h-5 text-green-600 dark:text-green-400" />
                </div>
                <div className="p-4 flex-1">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Név
                  </label>
                  <p className="text-sm text-gray-900 dark:text-gray-100">{previewTemplate.name}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Kategória
                  </label>
                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${getCategoryInfo(previewTemplate.category).color} border-current`}>
                    {getCategoryInfo(previewTemplate.category).label}
                  </span>
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Leírás
                  </label>
                  <p className="text-sm text-gray-900 dark:text-gray-100">{previewTemplate.description}</p>
                </div>
              </div>
                </div>
              </div>
            </Card>

            {/* Variables */}
            {previewTemplate.variables.length > 0 && (
              <div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
                  Változók ({previewTemplate.variables.length})
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {previewTemplate.variables.map((variable, index) => (
                    <div key={index} className="bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg p-3">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                          {variable.label}
                        </span>
                        {variable.required && (
                          <span className="text-xs text-red-600 dark:text-red-400">Kötelező</span>
                        )}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 space-y-1">
                        <p>Kulcs: <code className="bg-gray-100 dark:bg-gray-800 px-1 rounded">{variable.key}</code></p>
                        <p>Típus: {variable.type}</p>
                        {variable.defaultValue && <p>Alapértelmezett: {variable.defaultValue}</p>}
                        {variable.description && <p>Leírás: {variable.description}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Template content */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
                Sablon tartalom
              </h3>
              <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
                <pre className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap overflow-x-auto">
                  {previewTemplate.content}
                </pre>
              </div>
            </div>
            </div>
          </div>
        )}
      </Modal>
    </Modal>
  );
};

export default TemplateManager;
